import { Link, NavLink } from 'react-router-dom';
import { config } from '../config';
import { useAuth } from '../auth/useAuth';
import { type AreaId, type NavArea, areaById, visibleItems, visibleTabAreas } from './navRegistry';

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  /**
   * Story #22190 (BR-MOB-010) — `docked` at 1024px and up (today's layout, unchanged); `overlay`
   * below it, where the sidebar is a drawer that stays closed until the hamburger opens it.
   */
  mode?: 'docked' | 'overlay';
  /**
   * Story #22190 (BR-MOB-001) — on a phone the top bar has no area strip, so the drawer starts with
   * the areas as a two-column grid above the selected area's items.
   */
  showAreaGrid?: boolean;
  /** The area selected in the top menu — the sidebar shows only this area's submenu (#14378). */
  activeAreaId: AreaId;
  /** Select an area from the drawer's area grid (phone only). */
  onSelectArea?: (id: AreaId) => void;
  onCollapse: () => void;
  onMobileClose: () => void;
}

const ENV = config.environment;
const VERSION = config.appVersion;

const envBadge: Record<string, { text: string; classes: string; dot: string }> = {
  Development: { text: 'DEV', classes: 'bg-green-100 text-green-700 border-green-300', dot: 'bg-green-500' },
  Staging: { text: 'STAGING', classes: 'bg-amber-100 text-amber-700 border-amber-300', dot: 'bg-amber-500' },
  Production: { text: 'PROD', classes: 'bg-red-100 text-red-700 border-red-300', dot: 'bg-red-500' },
};

/**
 * Story #22190 (BR-MOB-001) — the environment badge must stay visible at every width. The docked
 * sidebar carries it from 1024px up; below that the sidebar is a closed drawer, so the top bar
 * renders this same badge (same colours, same text) beside the hamburger.
 */
export function EnvironmentBadge({ className = '' }: { className?: string }) {
  const badge = envBadge[ENV] || envBadge.Development;
  return (
    <span
      data-testid="env-badge"
      className={`inline-flex flex-shrink-0 items-center justify-center whitespace-nowrap rounded-md border px-2 py-1 text-xs font-medium tracking-wide ${badge.classes} ${className}`}
    >
      {badge.text}
    </span>
  );
}

// Story #22190 (BR-MOB-001) — one cell of the phone drawer's area grid. Same behaviour as the top
// bar's area tab: go to the area's first visible route and select the area. The drawer stays open
// so the selected area's items (listed below the grid) can be picked; picking an item navigates and
// closes it.
function AreaGridCell({
  area,
  isActive,
  href,
  onSelect,
}: {
  area: NavArea;
  isActive: boolean;
  href?: string;
  onSelect: () => void;
}) {
  const className = `flex min-h-11 items-center justify-center gap-1.5 rounded-lg border px-2 text-center text-[13px] font-medium transition-colors ${
    isActive
      ? 'border-indigo-600 bg-indigo-600 text-white'
      : 'border-slate-700 text-slate-200 hover:bg-slate-800'
  }`;
  const content = (
    <>
      <span aria-hidden="true">{area.icon}</span>
      <span>{area.label}</span>
    </>
  );
  if (!href) {
    return (
      <button type="button" onClick={onSelect} aria-current={isActive ? 'page' : undefined} className={className}>
        {content}
      </button>
    );
  }
  return (
    <Link to={href} onClick={onSelect} aria-current={isActive ? 'page' : undefined} className={className}>
      {content}
    </Link>
  );
}

// Story #14378 — the sidebar is now the ACTIVE AREA's submenu, not the whole flat nav. The area
// map, the RBAC gates and the route -> area resolution all live in navRegistry.ts (single source
// of truth, BR-NAV-006); this component only renders. Areas are switched from the Topbar tabs.
export function Sidebar({
  collapsed: collapsedProp,
  mobileOpen,
  mode = 'docked',
  showAreaGrid = false,
  activeAreaId,
  onSelectArea,
  onCollapse,
  onMobileClose,
}: SidebarProps) {
  const badge = envBadge[ENV] || envBadge.Development;
  const { roles } = useAuth();

  const area = areaById(activeAreaId);
  const items = visibleItems(area, roles);

  const overlay = mode === 'overlay';
  // The icon-only rail is a docked-desktop affordance; the overlay drawer is always full width with
  // labels, even if the rail was collapsed before the window narrowed.
  const collapsed = collapsedProp && !overlay;
  const areaGrid = overlay && showAreaGrid;
  const firstRouteOf = (a: NavArea): string | undefined =>
    visibleItems(a, roles).find(item => item.path)?.path;

  // Docked: in flow, exactly as before. Overlay: fixed above the content, off-canvas and
  // `invisible` (out of the tab order and the accessibility tree) until the hamburger opens it.
  const placement = overlay
    ? `fixed inset-y-0 left-0 z-50 ${mobileOpen ? 'translate-x-0 visible' : '-translate-x-full invisible'}`
    : 'relative translate-x-0';

  return (
    <aside
      data-mode={mode}
      data-open={overlay ? String(mobileOpen) : undefined}
      className={`
      ${placement} h-full
      ${collapsed ? 'w-16' : 'w-64'}
      flex flex-col bg-slate-900 border-r border-slate-800
      transition-all duration-250 ease-in-out
    `}>
      {/* Logo + environment badge */}
      <div className="flex flex-col items-stretch px-4 pt-4 pb-2">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-start'} gap-2`}>
          <span className="inline-block w-7 h-7 rounded-md bg-gradient-to-br from-indigo-600 to-purple-600 flex-shrink-0" aria-hidden="true" />
          {!collapsed && <span className="font-semibold text-white">TSP - XCentral</span>}
        </div>
        {collapsed ? (
          <div className={`mt-2 self-center w-3 h-3 rounded-full ${badge.dot}`} title={badge.text} />
        ) : (
          <span className={`mt-2 flex items-center justify-center rounded-md border px-2 py-1 text-xs font-medium tracking-wide ${badge.classes}`}>
            {badge.text}
          </span>
        )}
      </div>

      {/* Phone drawer: the areas as a two-column grid, above the selected area's items (BR-MOB-001) */}
      {areaGrid && (
        <nav aria-label="All areas" className="grid grid-cols-2 gap-1.5 border-b border-slate-800 px-3 py-2.5">
          {visibleTabAreas(roles).map(a => (
            <AreaGridCell
              key={a.id}
              area={a}
              isActive={activeAreaId === a.id}
              href={firstRouteOf(a)}
              onSelect={() => onSelectArea?.(a.id)}
            />
          ))}
        </nav>
      )}

      {/* Active area's submenu */}
      <nav className="flex-1 overflow-y-auto px-2 py-4" aria-label={`${area.label} menu`}>
        {collapsed ? (
          <div className="mx-2 mb-2 border-t border-slate-800" aria-hidden />
        ) : (
          <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {area.label}
          </div>
        )}
        <div className="space-y-1">
          {items.map(item =>
            item.future ? (
              // Not yet built (see navRegistry `future`) — a visibly-disabled affordance so the
              // approved area map stays complete without a dead link or a stub page.
              <div
                key={item.label}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-500 cursor-default
                  ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? `${item.label} (coming soon)` : undefined}
              >
                <span className="text-base">{item.icon}</span>
                {!collapsed && (
                  <>
                    <span>{item.label}</span>
                    <span className="ml-auto rounded bg-slate-800 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-slate-400">
                      Soon
                    </span>
                  </>
                )}
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path!}
                // #16040 — an index item (`/xcentral-hub`) prefix-matches all of its siblings, so
                // without `end` it stays lit on every child page and the sidebar stops telling you
                // where you are. Only items that declare `exact` opt in; nothing else changes.
                end={item.exact}
                onClick={onMobileClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors
                ${isActive ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}
                ${collapsed ? 'justify-center' : ''}`
                }
                title={collapsed ? (item.chip ? `${item.label} (${item.chip})` : item.label) : undefined}
              >
                <span className="text-base">{item.icon}</span>
                {!collapsed && <span>{item.label}</span>}
                {/* A chipped item IS routable (unlike `future` above) — the chip says the approved
                    IA lists this page but the prototype does not draw it yet (#16040 AC5). */}
                {!collapsed && item.chip && (
                  <span className="ml-auto rounded bg-slate-800 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-slate-400">
                    {item.chip}
                  </span>
                )}
              </NavLink>
            ),
          )}
        </div>
      </nav>

      {/* Collapse toggle (docked desktop only) */}
      {!overlay && (
        <button
          onClick={onCollapse}
          className="hidden lg:flex items-center justify-center h-10 border-t border-slate-800 text-slate-400 hover:text-slate-200"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? '→' : '←'}
        </button>
      )}

      {/* App version */}
      <div
        className={`border-t border-slate-800 px-3 py-2 text-slate-400 ${collapsed ? 'text-center' : ''}`}
        title={`Version ${VERSION}`}
      >
        <span className="text-[10px] font-mono tracking-tight">
          {collapsed ? 'v' : `v ${VERSION}`}
        </span>
      </div>
    </aside>
  );
}
